import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router';
import { Alert, Box, Button, Stack, TextField, Typography } from '@mui/material';
import LoginIcon from '@mui/icons-material/LoginOutlined';
import { AuroraBackground } from '@/components/AuroraBackground';
import { GlassCard } from '@/components/GlassCard';
import { Brand } from '@/components/Brand';
import { ThemeToggle } from '@/components/ThemeToggle';
import { api, ApiError } from '@/lib/api';

export default function LoginPage() {
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      await api.post('/api/auth/login', { username, password });
      navigate('/', { replace: true });
    } catch (err) {
      const msg = err instanceof ApiError && err.status === 401
        ? 'ຊື່ຜູ້ໃຊ້ ຫຼື ລະຫັດຜ່ານ ບໍ່ຖືກຕ້ອງ'
        : 'ເຂົ້າສູ່ລະບົບບໍ່ສຳເລັດ ກະລຸນາລອງໃໝ່';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Box sx={{ minHeight: '100dvh', display: 'grid', placeItems: 'center', p: 2 }}>
      <AuroraBackground />
      <Box sx={{ position: 'fixed', top: 16, right: 16 }}><ThemeToggle /></Box>
      <GlassCard sx={{ width: '100%', maxWidth: 420 }}>
        <Stack spacing={3} component="form" onSubmit={onSubmit}
          sx={{
            '@keyframes riseIn': {
              from: { opacity: 0, transform: 'translateY(12px)' },
              to: { opacity: 1, transform: 'none' },
            },
            '@media (prefers-reduced-motion: reduce)': {
              '& [data-stagger]': { animation: 'none !important' },
            },
          }}
        >
          <Box data-stagger sx={{ animation: 'riseIn 500ms cubic-bezier(0.16,1,0.3,1) both', animationDelay: '60ms' }}>
            <Brand />
          </Box>
          <Typography variant="h4"
            data-stagger
            sx={{ animation: 'riseIn 500ms cubic-bezier(0.16,1,0.3,1) both', animationDelay: '140ms' }}
          >
            ເຂົ້າສູ່ລະບົບ
          </Typography>
          {error && <Alert severity="error" variant="outlined">{error}</Alert>}
          <TextField
            label="ຊື່ຜູ້ໃຊ້" value={username} autoFocus autoComplete="username"
            onChange={(e) => setUsername(e.target.value)} fullWidth required
            data-stagger
            sx={{ animation: 'riseIn 500ms cubic-bezier(0.16,1,0.3,1) both', animationDelay: '220ms' }}
          />
          <TextField
            label="ລະຫັດຜ່ານ" type="password" value={password} autoComplete="current-password"
            onChange={(e) => setPassword(e.target.value)} fullWidth required
            data-stagger
            sx={{ animation: 'riseIn 500ms cubic-bezier(0.16,1,0.3,1) both', animationDelay: '300ms' }}
          />
          <Button type="submit" variant="contained" size="large" loading={loading}
            startIcon={<LoginIcon />} disabled={!username || !password}
            data-stagger
            sx={{ animation: 'riseIn 500ms cubic-bezier(0.16,1,0.3,1) both', animationDelay: '380ms' }}
          >
            ເຂົ້າສູ່ລະບົບ
          </Button>
        </Stack>
      </GlassCard>
    </Box>
  );
}
